import { BlockchainIcon } from "@/app/blockchains/blockchain-icon/blockchain-icon"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { useBlockchain } from "@/app/blockchains/use-blockchain"
import { useCheckoutProvider } from "@/app/coinflow/use-checkout-provider"
import { CrossmintEvent } from "@/app/crossmint/crossmint-events"
import { useCrossmintModal } from "@/app/crossmint/crossmint-modal-provider"
import { getCrossmintEventContext } from "@/app/crossmint/get-crossmint-event-context"
import { useEvent } from "@/app/hooks/use-event"
import { NftsToBuyMoleculeReatom } from "@/app/nft/buy/nfts-to-buy/molecule.reatom"
import { useIsCrossmintEnabled } from "@/app/nft/order/widget/actions/blockchains/evm/use-brand-collection"
import { useConnectStatus } from "@/app/nft/order/widget/actions/use-connect-status"
import { PurchasePriceQuote } from "@/app/nft/price-quote/price-quote"
import { PriceWrapper } from "@/app/price"
import { useUsdPrice } from "@/app/price/hooks"
import { useEvmServiceFee } from "@/app/sdk/action-molecules/eclipse/fees-molecule"
import { useBatchBuy } from "@/app/sdk/action-molecules/order/use-batch-buy"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import { useUserBalance } from "@/app/wallet-connect/hooks"

import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  cn,
} from "@rarible_int/anaconda_ui_components"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useMemo, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useCollectionBlockchain } from "../store/use-collection"
import { useCollection } from "../store/use-collection"
import { BuyCollectionButtonContent } from "./buy-collection-content-button"

export const BuySelectedItemsButton = reatomComponent(() => {
  const { selectedNftsToBuy, selectedNftsTotalPrice } = useMolecule(
    NftsToBuyMoleculeReatom,
  )
  const selectedNfts = selectedNftsToBuy()
  const totalPrice = selectedNftsTotalPrice()
  const { status, unionAddress } = useWalletConnect()
  const func = useConnectStatus(() => {})

  const blockchain = useCollectionBlockchain()
  const collection = useCollection()
  const { isEvmBlockchain: reatomIsEvmBlockchain } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const getIsEvmBlockchain = reatomIsEvmBlockchain()
  const isEclipseBlockchain = false // Eclipse support removed in Reatom version
  const isEvmBlockchain = getIsEvmBlockchain(blockchain)

  // Check if Crossmint is enabled for this collection
  const crossmintCheckoutEnabled = useIsCrossmintEnabled(collection?.id || "")

  // Crossmint modal hooks
  const { openModal: openCrossmintModal, closeModal: closeCrossmintModal } =
    useCrossmintModal()

  // Coinflow card checkout is single-item only, so batch card purchases
  // stay on Crossmint's modal only when Crossmint is the active provider.
  const checkoutProvider = useCheckoutProvider()

  // Wallet modal state
  const [showWalletModal, setShowWalletModal] = useState(false)
  const [walletModalPaymentMethod, setWalletModalPaymentMethod] = useState<
    "card" | "wallet"
  >("wallet")

  // Get user balance and blockchain info
  const userBalance = useUserBalance()
  const { name: blockchainName } = useBlockchain(blockchain)

  // Get add funds link
  const { reatomAddFundsLink } = useMolecule(BlockchainsMoleculeReatom)
  const getAddFundsLink = reatomAddFundsLink()
  const addFundsLink = getAddFundsLink(blockchain)

  const logger = useMolecule(LoggerMolecule)

  // USD price
  const usdPrice = totalPrice ? useUsdPrice(totalPrice) : undefined

  // Fee calculation with error handling
  let evmServiceFeeQuery: ReturnType<typeof useEvmServiceFee> | undefined
  let safeServiceFeeQuery: {
    isLoading: boolean
    data: number | undefined
    error: Error | null
  }

  try {
    evmServiceFeeQuery = useEvmServiceFee(blockchain, totalPrice)
    safeServiceFeeQuery = {
      isLoading: evmServiceFeeQuery.isLoading,
      data: evmServiceFeeQuery.data,
      error: evmServiceFeeQuery.error,
    }
  } catch (error) {
    console.warn("Fee calculation failed, using fallback:", error)
    safeServiceFeeQuery = {
      isLoading: false,
      data: undefined,
      error: new Error("Fee calculation unavailable"),
    }
  }

  // Get batch buy action with chain switching support
  const { mutate: batchBuyMutate, isPending } = useBatchBuy(
    selectedNfts,
    totalPrice,
  )

  const mutate = useEvent(() => {
    if (selectedNfts.length === 0) return
    batchBuyMutate()
  })

  // Wallet fallback handler - called when user clicks "Wallet" in Crossmint modal
  const handleWalletFallback = useEvent(() => {
    const fallbackContext: EventContext = {
      ...getCrossmintEventContext({
        nfts: selectedNfts,
        paymentMethod: "crossmint_wallet",
      }),
      flow: Flow.CrossmintSecondaryCheckout,
      status: status || "unknown",
    }

    logger?.api.pushEvent(CrossmintEvent.WalletFallback, fallbackContext)
    amplitude.track(CrossmintEvent.WalletFallback, fallbackContext)

    closeCrossmintModal()
    setWalletModalPaymentMethod("wallet")
    if (status === "connected") {
      setShowWalletModal(true)
    } else {
      // If not connected, trigger the connect flow
      func()
    }
  })

  const handleWalletBuy = useEvent(() => {
    const walletBuyContext: EventContext = {
      ...getCrossmintEventContext({
        nfts: selectedNfts,
        paymentMethod: "crossmint_wallet",
      }),
      flow: Flow.CrossmintSecondaryCheckout,
    }

    logger?.api.pushEvent(CrossmintEvent.CheckoutStarted, walletBuyContext)
    amplitude.track(CrossmintEvent.CheckoutStarted, walletBuyContext)

    setShowWalletModal(false)
    mutate()
  })

  const handleSwitchToCard = useEvent(() => {
    setWalletModalPaymentMethod("card")
    setShowWalletModal(false)

    // Coinflow card checkout is single-item only; batches >15 exceed
    // Crossmint's limit. Both fall back to direct wallet purchase.
    if (checkoutProvider === "coinflow" || selectedNfts.length > 15) {
      mutate()
      return
    }

    openCrossmintModal({
      nfts: selectedNfts,
      collectionName: collection?.name,
      onWalletFallback: handleWalletFallback,
    })
  })

  const handleClick = useEvent(() => {
    if (status !== "connected") {
      func()
      return
    }
    // For EVM blockchains with Crossmint enabled, show Crossmint modal
    if (
      isEvmBlockchain &&
      crossmintCheckoutEnabled &&
      selectedNfts.length > 0
    ) {
      // Coinflow card checkout is single-item only; batches >15 exceed
      // Crossmint's limit. Both fall back to direct wallet purchase.
      if (checkoutProvider === "coinflow" || selectedNfts.length > 15) {
        mutate()
        return
      }

      openCrossmintModal({
        nfts: selectedNfts,
        collectionName: collection?.name,
        onWalletFallback: handleWalletFallback,
      })
    } else {
      // Direct wallet purchase for non-Crossmint or Eclipse blockchains
      mutate()
    }
  })

  if (!totalPrice) return null

  const buttonElement = (
    <Button className="bg-success/20 text-white text-xs" onClick={handleClick}>
      <BuyCollectionButtonContent price={totalPrice} />
    </Button>
  )

  return (
    <>
      <div>{buttonElement}</div>

      {/* Enhanced Wallet Checkout Modal for Batch Purchases */}
      <Dialog open={showWalletModal} onOpenChange={setShowWalletModal}>
        <DialogContent className="max-w-md border-border bg-background">
          <DialogHeader>
            <DialogTitle className="text-center font-medium text-foreground">
              <Trans>Checkout ({selectedNfts.length} items)</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Batch Info Card */}
            <Card className="border-border bg-card">
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-card-foreground text-xs">
                    <Trans>{selectedNfts.length} Items Selected</Trans>
                  </p>
                  <div className="text-right">
                    <p className="font-medium text-card-foreground text-sm">
                      {totalPrice?.amount}{" "}
                      {totalPrice?.currency.abbreviation?.toUpperCase()}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      ${usdPrice?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                </div>
                {selectedNfts.length <= 5 && (
                  <div className="flex gap-1 overflow-x-auto">
                    {selectedNfts.slice(0, 5).map((item) => (
                      <img
                        key={item.id}
                        src={item.image}
                        alt={item.name}
                        className="h-12 w-12 flex-shrink-0 rounded object-cover"
                      />
                    ))}
                    {selectedNfts.length > 5 && (
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded bg-muted text-xs">
                        +{selectedNfts.length - 5}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Method Tabs */}
            <div className="flex gap-0.5 rounded-full border border-border bg-card p-0.5">
              <button
                type="button"
                className={cn(
                  "flex-1 rounded-sm px-3 py-1.5 text-center font-medium text-xs transition-all",
                  walletModalPaymentMethod === "card"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={handleSwitchToCard}
              >
                <Trans>Card</Trans>
              </button>

              <button
                type="button"
                className={cn(
                  "flex-1 rounded-sm px-3 py-1.5 text-center font-medium text-xs transition-all",
                  walletModalPaymentMethod === "wallet"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setWalletModalPaymentMethod("wallet")}
              >
                <Trans>Wallet</Trans>
              </button>
            </div>

            {/* Current Payment Currency */}
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground text-sm">
                <Trans>Current payment currency</Trans>
              </p>
              <Card className="border-border bg-card">
                <CardContent className="flex items-center space-x-3 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                    <BlockchainIcon
                      blockchain={blockchain}
                      className="h-4 w-4"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-card-foreground">
                      {blockchainName ||
                        totalPrice?.currency.abbreviation?.toUpperCase() ||
                        "ETH"}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      <Trans>Balance:</Trans>{" "}
                      <PriceWrapper
                        amount={userBalance?.toString() || "0"}
                        currency={totalPrice?.currency}
                        className="text-muted-foreground"
                      />
                    </p>
                  </div>
                </CardContent>
              </Card>
              {userBalance &&
                totalPrice &&
                Number.parseFloat(userBalance.toString()) <
                  Number.parseFloat(totalPrice.amount) && (
                  <p className="text-red-500 text-sm">
                    <Trans>
                      Insufficient funds on {blockchainName || blockchain} Chain
                    </Trans>
                  </p>
                )}
            </div>

            {/* Price Quote with Fees */}
            {totalPrice && (
              <div className="space-y-2">
                <ErrorBoundary
                  fallback={
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">
                          <Trans>Price</Trans>
                        </span>
                        <span className="font-medium">
                          <PriceWrapper
                            amount={totalPrice.amount}
                            currency={totalPrice.currency}
                          />
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-yellow-600">
                        <Trans>Fee calculation unavailable</Trans>
                      </div>
                    </div>
                  }
                  onError={(error) => {
                    console.warn(
                      "PriceQuote ErrorBoundary caught error:",
                      error,
                    )
                  }}
                >
                  <PurchasePriceQuote
                    nftId={selectedNfts[0]?.id || "batch"}
                    price={totalPrice}
                    serviceFeeQuery={safeServiceFeeQuery}
                  />
                </ErrorBoundary>
                {safeServiceFeeQuery.error && (
                  <div className="rounded-md bg-yellow-50 p-3 text-sm">
                    <p className="text-yellow-800">
                      <Trans>
                        Fee information temporarily unavailable. Proceeding with
                        base price.
                      </Trans>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Wallet Purchase Button */}
            <div className="space-y-4">
              <Button
                onClick={() => {
                  const hasInsufficientFunds =
                    userBalance &&
                    totalPrice &&
                    Number.parseFloat(userBalance.toString()) <
                      Number.parseFloat(totalPrice.amount)

                  const walletModalContext: EventContext = {
                    ...getCrossmintEventContext({
                      nfts: selectedNfts,
                      paymentMethod: "crossmint_wallet",
                    }),
                    flow: Flow.CrossmintSecondaryCheckout,
                    hasInsufficientFunds:
                      hasInsufficientFunds?.toString() || "false",
                    userBalance: userBalance?.toString() || "0",
                    addFundsLink: addFundsLink ? "true" : "false",
                  }

                  if (hasInsufficientFunds) {
                    try {
                      if (addFundsLink) {
                        window.open(
                          addFundsLink,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      } else {
                        handleWalletBuy()
                      }
                    } catch (error) {
                      console.warn("Failed to open add funds link:", error)
                      handleWalletBuy()
                    }
                  } else {
                    handleWalletBuy()
                  }
                }}
                loading={isPending}
                className="w-full bg-brand text-black hover:bg-brand/90"
              >
                {userBalance &&
                totalPrice &&
                Number.parseFloat(userBalance.toString()) <
                  Number.parseFloat(totalPrice.amount) ? (
                  <Trans>Add funds and pay</Trans>
                ) : (
                  <Trans>Buy Now</Trans>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}, "BuySelectedItemsButton")
